import { useCallback, useEffect, useMemo, useState } from "react";

import { apiClient } from "../api/client.js";
import { formatUkDateTime } from "../lib/dates.js";

/**
 * Remote shell commands for one device. Platform-admin only — the caller gates rendering, and
 * both endpoints are `requirePlatformAdmin` regardless.
 *
 * Two things about this channel shape the UI, and both come from
 * `device-setup/lessons-learned/DISPLAY-AND-TOUCH-ROTATION.md`:
 *
 *   - Commands ride the heartbeat, so a result arrives on the device's *next* check-in — up to
 *     a minute later. "Pending" is the normal state for a while, not a failure, which is why the
 *     history is the main surface here rather than an afterthought to the buttons.
 *   - The executor gives a command 30 seconds and keeps the last 4096 bytes of its output.
 */

/** Mirrors `v1_admin_DeviceCommand`. */
interface DeviceCommand {
  id: string;
  device_id: string;
  idempotency_key: string;
  command: string;
  status: string;
  exit_code?: number;
  output?: string;
  created_at: string;
  delivered_at?: string;
  completed_at?: string;
}

/** Statuses the device has not reported on yet — while any is in flight, keep polling. */
const IN_FLIGHT = new Set(["pending", "delivered"]);

interface PresetAction {
  key: string;
  label: string;
  /** Shown under the button; says what it does and what it costs. */
  description: string;
  script: string;
  /** Extra line in the confirm, for actions that interrupt play. */
  warning?: string;
}

/**
 * The scripts are here rather than typed by hand each time because getting them slightly wrong
 * is the whole hazard. Both are the measured, known-good forms from the rotation doc.
 */
const PRESETS: PresetAction[] = [
  {
    key: "fix-rotation",
    label: "Fix rotation",
    description:
      "Pins the display to upright and stops it flipping on reboot (configuration A). " +
      "No reboot, no /hddata write.",
    script: [
      "cmd window fixed-to-user-rotation enabled",
      "settings put system user_rotation 0",
    ].join("\n"),
    warning:
      "Also set the device's screen_rotation config to 0, or the next config apply will undo " +
      "user_rotation. Note screen_rotation is in degrees and only 180 is recognised — " +
      "anything else becomes 0.",
  },
  {
    key: "restart",
    label: "Restart device",
    description: "Reboots the table. It will be unusable for a minute or two.",
    /*
     * Backgrounded so the executor's stdout pipe closes and an exit code is recorded. A plain
     * `reboot` kills the process that would report the result, so the command sits at
     * "delivered" for ever even though it worked.
     */
    script: "(sleep 3; /system/bin/reboot) >/dev/null 2>&1 &",
    warning: "Any play session in progress on this table will be interrupted.",
  },
];

function statusClasses(status: string): string {
  if (status === "completed") return "text-green-400";
  if (status === "failed") return "text-red-400";
  if (IN_FLIGHT.has(status)) return "text-yellow-400";
  return "text-text-secondary";
}

function formatWhen(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatUkDateTime(iso);
}

export function DeviceCommands({
  deviceId,
  deviceName,
  serialNumber,
}: {
  deviceId: string;
  deviceName: string;
  /** Typed back by the operator to arm the advanced runner. */
  serialNumber: string;
}) {
  const [commands, setCommands] = useState<DeviceCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<PresetAction | null>(null);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [script, setScript] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [typedSerial, setTypedSerial] = useState("");
  const [advancedConfirming, setAdvancedConfirming] = useState(false);

  const fetchCommands = useCallback(async () => {
    if (!deviceId) return;
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/commands"]["get"]({
        parameters: { id: deviceId },
        body: null,
      });
      if (res.code === 200) setCommands(res.body as DeviceCommand[]);
    } catch {
      // Leave the last good list up; a failed poll is not worth blanking the table for.
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    void fetchCommands();
  }, [fetchCommands]);

  const anyInFlight = useMemo(() => commands.some((c) => IN_FLIGHT.has(c.status)), [commands]);

  /**
   * Poll only while something is outstanding. Results land on the device's next heartbeat, so
   * this is a wait measured in tens of seconds; polling when nothing is queued would just be
   * traffic.
   */
  useEffect(() => {
    if (!anyInFlight) return;
    const t = setInterval(() => void fetchCommands(), 5000);
    return () => clearInterval(t);
  }, [anyInFlight, fetchCommands]);

  const send = useCallback(
    async (label: string, commandScript: string) => {
      setSending(label);
      setMessage(null);
      /* Whichever path sent, close the other's confirm — a stale prompt sitting above a success
         message reads as though the prompt is what succeeded. */
      setConfirming(null);
      setAdvancedConfirming(false);
      try {
        const res = await apiClient["/api/v1/admin/devices/{id}/commands"]["post"]({
          parameters: { id: deviceId },
          body: {
            // Per click, not per action: a second restart must not collide with the first.
            idempotency_key: crypto.randomUUID(),
            command: commandScript,
          },
        });
        if (res.code === 201) {
          setMessage({
            type: "success",
            text: `${label} queued. The device picks it up on its next heartbeat — up to a minute.`,
          });
          await fetchCommands();
        } else if (res.code === 409) {
          setMessage({ type: "error", text: "That command was already queued." });
        } else {
          setMessage({ type: "error", text: `Failed to queue ${label} (${res.code}).` });
        }
      } catch {
        setMessage({ type: "error", text: `Failed to queue ${label}.` });
      } finally {
        setSending(null);
      }
    },
    [deviceId, fetchCommands],
  );

  const cancelCommand = useCallback(
    async (commandId: string) => {
      setCancelling(commandId);
      setMessage(null);
      try {
        const res = await apiClient["/api/v1/admin/devices/{id}/commands/{commandId}/cancel"][
          "post"
        ]({
          parameters: { id: deviceId, commandId },
          body: null,
        });
        if (res.code === 200) {
          setMessage({
            type: "success",
            text: "Command cancelled. It will not be delivered again.",
          });
          await fetchCommands();
        } else {
          const body = res.body as { message?: string };
          setMessage({ type: "error", text: body.message ?? "Failed to cancel command." });
          await fetchCommands();
        }
      } catch {
        setMessage({ type: "error", text: "Failed to cancel command." });
      } finally {
        setCancelling(null);
      }
    },
    [deviceId, fetchCommands],
  );

  const advancedArmed =
    understood && typedSerial.trim() === serialNumber && script.trim().length > 0;

  return (
    <section className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-text-primary">💻 Remote Commands</h2>
        <button
          onClick={() => void fetchCommands()}
          className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
        >
          Refresh
        </button>
      </div>

      <p className="text-sm text-text-secondary mb-4">
        Runs as root on the device. Commands are delivered on the device&apos;s next heartbeat, so a
        result can take up to a minute to appear.
      </p>

      <div className="flex flex-wrap gap-3 mb-2">
        {PRESETS.map((preset) => (
          <div key={preset.key} className="flex-1 min-w-[240px]">
            <button
              onClick={() => {
                setConfirming(preset);
                setMessage(null);
              }}
              disabled={sending !== null}
              className="w-full px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending === preset.label ? "Queueing..." : preset.label}
            </button>
            <p className="text-xs text-text-secondary mt-1">{preset.description}</p>
          </div>
        ))}
      </div>

      {confirming && (
        <div className="mt-4 p-4 rounded-md border border-yellow-500/50 bg-yellow-500/10">
          <p className="text-sm text-text-primary font-medium mb-1">
            Run &ldquo;{confirming.label}&rdquo; on {deviceName}?
          </p>
          {confirming.warning && (
            <p className="text-xs text-yellow-300 mb-2">{confirming.warning}</p>
          )}
          <pre className="text-xs text-text-secondary bg-surface border border-border rounded p-2 mb-3 overflow-x-auto whitespace-pre-wrap">
            {confirming.script}
          </pre>
          <div className="flex gap-3">
            <button
              onClick={() => {
                const preset = confirming;
                setConfirming(null);
                void send(preset.label, preset.script);
              }}
              className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
            >
              Confirm
            </button>
            <button
              onClick={() => setConfirming(null)}
              className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {message && (
        <p
          className={`text-sm mt-3 ${message.type === "success" ? "text-green-400" : "text-red-400"}`}
        >
          {message.text}
        </p>
      )}

      {/* ── Advanced ── */}
      <div className="mt-6 pt-4 border-t border-border">
        <button
          onClick={() => setAdvancedOpen((v) => !v)}
          className="text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          {advancedOpen ? "▾" : "▸"} Advanced: run an arbitrary command
        </button>

        {advancedOpen && (
          <div className="mt-3 p-4 rounded-md border border-red-500/50 bg-red-500/10">
            <p className="text-sm text-red-300 font-medium mb-2">
              This runs whatever you type as root on a table in a venue.
            </p>
            <ul className="text-xs text-text-secondary list-disc pl-5 mb-3 space-y-1">
              <li>There is no dry run, no undo, and no confirmation from the device first.</li>
              <li>
                A command that breaks networking or the kiosk app can leave the table unreachable
                and needing a physical visit.
              </li>
              <li>30 second timeout. Only the last 4096 bytes of output are kept.</li>
              <li>
                A command that reboots must be backgrounded, or it kills the process that would
                report its result.
              </li>
              <li>
                The right end state often differs per table — a script derived from one table can
                break another.
              </li>
            </ul>

            <textarea
              value={script}
              onChange={(e) => {
                setScript(e.target.value);
                setAdvancedConfirming(false);
              }}
              rows={5}
              spellCheck={false}
              aria-label="Command script"
              placeholder="sh script, run via su 0 sh -c"
              className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary font-mono text-xs focus:outline-none focus:border-accent transition-colors"
            />

            <label className="flex items-start gap-2 mt-3 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => {
                  setUnderstood(e.target.checked);
                  setAdvancedConfirming(false);
                }}
                className="mt-0.5"
              />
              <span>
                I have read the command above and understand it runs as root on {deviceName} and
                cannot be undone.
              </span>
            </label>

            <div className="mt-3">
              <label className="block text-xs text-text-secondary mb-1">
                Type the device serial{" "}
                <span className="font-mono text-text-primary">{serialNumber}</span> to confirm
              </label>
              <input
                type="text"
                value={typedSerial}
                onChange={(e) => {
                  setTypedSerial(e.target.value);
                  setAdvancedConfirming(false);
                }}
                aria-label="Confirm device serial"
                autoComplete="off"
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary font-mono text-xs focus:outline-none focus:border-accent transition-colors"
              />
            </div>

            {!advancedConfirming ? (
              <button
                onClick={() => setAdvancedConfirming(true)}
                disabled={!advancedArmed || sending !== null}
                className="mt-3 px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Run command
              </button>
            ) : (
              <div className="mt-3">
                <p className="text-sm text-red-300 mb-2">
                  Last check — run this on {deviceName} now?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setAdvancedConfirming(false);
                      void send("Command", script);
                    }}
                    className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-500 transition-colors"
                  >
                    Yes, run it
                  </button>
                  <button
                    onClick={() => setAdvancedConfirming(false)}
                    className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── History ── */}
      <div className="mt-6 pt-4 border-t border-border">
        <h3 className="text-sm font-medium text-text-secondary mb-2">
          History
          {anyInFlight && <span className="ml-2 text-xs text-yellow-400">awaiting heartbeat…</span>}
        </h3>
        {loading ? (
          <p className="text-sm text-text-secondary">Loading...</p>
        ) : commands.length === 0 ? (
          <p className="text-sm text-text-secondary">No commands have been sent to this device.</p>
        ) : (
          <div className="space-y-2">
            {commands.map((cmd) => (
              <details key={cmd.id} className="bg-surface border border-border rounded-md">
                <summary className="px-3 py-2 cursor-pointer text-sm text-text-primary flex items-center justify-between gap-3">
                  <span className="font-mono text-xs truncate">
                    {cmd.command.split("\n")[0]}
                    {cmd.command.includes("\n") ? " …" : ""}
                  </span>
                  <span className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs ${statusClasses(cmd.status)}`}>{cmd.status}</span>
                    {cmd.exit_code !== undefined && (
                      <span className="text-xs text-text-secondary">exit {cmd.exit_code}</span>
                    )}
                    <span className="text-xs text-text-secondary">
                      {formatWhen(cmd.created_at)}
                    </span>
                  </span>
                </summary>
                <div className="px-3 pb-3 space-y-2">
                  <div>
                    <p className="text-xs text-text-secondary mb-1">Command</p>
                    <pre className="text-xs text-text-primary bg-surface-raised border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap">
                      {cmd.command}
                    </pre>
                  </div>
                  {cmd.output !== undefined && (
                    <div>
                      <p className="text-xs text-text-secondary mb-1">
                        Output (last 4096 bytes kept)
                      </p>
                      <pre className="text-xs text-text-primary bg-surface-raised border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap">
                        {cmd.output || "(empty)"}
                      </pre>
                    </div>
                  )}
                  <dl className="text-xs text-text-secondary grid grid-cols-3 gap-2">
                    <div>
                      <dt>Queued</dt>
                      <dd className="text-text-primary">{formatWhen(cmd.created_at)}</dd>
                    </div>
                    <div>
                      <dt>Delivered</dt>
                      <dd className="text-text-primary">{formatWhen(cmd.delivered_at)}</dd>
                    </div>
                    <div>
                      <dt>Completed</dt>
                      <dd className="text-text-primary">{formatWhen(cmd.completed_at)}</dd>
                    </div>
                  </dl>
                  {IN_FLIGHT.has(cmd.status) && (
                    <div className="pt-2 border-t border-border">
                      <button
                        onClick={() => void cancelCommand(cmd.id)}
                        disabled={cancelling !== null}
                        className="px-3 py-1.5 text-xs rounded-md border border-red-500/50 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cancelling === cmd.id ? "Cancelling..." : "Cancel future delivery"}
                      </button>
                      {cmd.status === "delivered" && (
                        <p className="text-xs text-text-secondary mt-1">
                          This stops retries, but cannot recall a command already received by the
                          table.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
